// src/pages/SpecificVehicleReport.js
import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

// Backend base URL
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://157.180.43.56:5050'

// helpers
const num = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}
const fmt = (v) => num(v).toLocaleString()

// inclusive day count between two YYYY-MM-DD strings
const daysBetweenInclusive = (start, end) => {
  if (!start || !end) return 0
  const s = new Date(start)
  const e = new Date(end)
  const ms = e.getTime() - s.getTime()
  if (isNaN(ms) || ms < 0) return 0
  return Math.floor(ms / (1000 * 60 * 60 * 24)) + 1
}

export default function SpecificVehicleReport() {
  const [vehicles, setVehicles] = useState([])
  const [selectedVehicleId, setSelectedVehicleId] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]) // today
  const [reportData, setReportData] = useState([])
  const [vehicleInfo, setVehicleInfo] = useState(null)

  // period summary
  const [totalAmount, setTotalAmount] = useState(0)
  const [expectedAmount, setExpectedAmount] = useState(0)
  const [profitLoss, setProfitLoss] = useState(0)
  const [activeDays, setActiveDays] = useState(0)
  const [paidDays, setPaidDays] = useState(0)

  // overall summary
  const [expectedContractValue, setExpectedContractValue] = useState(0)
  const [totalCollectedOverall, setTotalCollectedOverall] = useState(0)

  const [loading, setLoading] = useState(false)
  const [loadingVehicles, setLoadingVehicles] = useState(true)
  const [errorMsg, setErrorMsg] = useState('')

  // pagination
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const printRef = useRef(null)

  // --- load vehicles from backend ---
  useEffect(() => {
    const loadVehicles = async () => {
      setLoadingVehicles(true)
      setErrorMsg('')
      try {
        const res = await fetch(`${API_BASE_URL}/api/vehicles`)
        if (!res.ok) {
          throw new Error(`Vehicles request failed with status ${res.status}`)
        }
        const data = await res.json()
        setVehicles(data.vehicles || [])
      } catch (err) {
        console.error('Vehicle fetch error:', err)
        setVehicles([])
        setErrorMsg('Could not load vehicles from backend.')
      } finally {
        setLoadingVehicles(false)
      }
    }

    loadVehicles()
  }, [])

  // when vehicle changes, auto-set From Date to contract_start
  const handleVehicleSelect = (vehicle) => {
    const vid = vehicle ? String(vehicle.id) : ''
    setSelectedVehicleId(vid)
    if (vehicle?.contract_start) {
      const startStr = String(vehicle.contract_start).slice(0, 10)
      setFromDate(startStr)
    } else {
      setFromDate('')
    }
  }

  // --- generate report using /api/reports/specific-vehicle ---
  const generateReport = async () => {
    if (!selectedVehicleId || !fromDate || !toDate) {
      alert('Please select a vehicle and date range')
      return
    }

    setLoading(true)
    setErrorMsg('')

    try {
      // 1) Period report (user-selected date range)
      const url = new URL('/api/reports/specific-vehicle', API_BASE_URL)
      url.searchParams.set('vehicleId', selectedVehicleId)
      url.searchParams.set('startDate', fromDate)
      url.searchParams.set('endDate', toDate)

      const res = await fetch(url.toString())
      if (!res.ok) {
        throw new Error(`Specific vehicle report failed with status ${res.status}`)
      }
      const data = await res.json()

      const vehicle = data.vehicle || null
      const payments = data.payments || []

      setVehicleInfo(vehicle)
      setReportData(payments)
      setPage(0)

      setTotalAmount(num(data.totalCollected))
      setExpectedAmount(num(data.expectedAmount))
      setProfitLoss(num(data.profitLoss))
      setActiveDays(num(data.activeDays))
      setPaidDays(num(data.paidDays))

      // 2) Overall / contract summary
      if (vehicle) {
        const contractStartStr = vehicle.contract_start
          ? String(vehicle.contract_start).slice(0, 10)
          : null
        const contractEndStr = vehicle.contract_end
          ? String(vehicle.contract_end).slice(0, 10)
          : toDate // fallback: up to current "To" date

        // expected for entire contract (or up to toDate)
        const contractDays = daysBetweenInclusive(contractStartStr, contractEndStr)
        const contractExpected =
          contractDays > 0 ? contractDays * num(vehicle.daily_rate) : 0
        setExpectedContractValue(contractExpected)

        // overall collected across contract period
        if (contractStartStr) {
          const overallUrl = new URL('/api/reports/specific-vehicle', API_BASE_URL)
          overallUrl.searchParams.set('vehicleId', selectedVehicleId)
          overallUrl.searchParams.set('startDate', contractStartStr)
          overallUrl.searchParams.set('endDate', contractEndStr)

          try {
            const resOverall = await fetch(overallUrl.toString())
            if (resOverall.ok) {
              const dataOverall = await resOverall.json()
              setTotalCollectedOverall(num(dataOverall.totalCollected))
            } else {
              // fallback: at least equal to this period
              setTotalCollectedOverall(num(data.totalCollected))
            }
          } catch (err2) {
            console.error('Overall-specific error:', err2)
            setTotalCollectedOverall(num(data.totalCollected))
          }
        } else {
          setTotalCollectedOverall(num(data.totalCollected))
        }
      } else {
        setExpectedContractValue(0)
        setTotalCollectedOverall(num(data.totalCollected))
      }
    } catch (err) {
      console.error('Error generating Specific Vehicle report:', err)
      setErrorMsg('Failed to generate Specific Vehicle report.')
      setVehicleInfo(null)
      setReportData([])
      setTotalAmount(0)
      setExpectedAmount(0)
      setProfitLoss(0)
      setActiveDays(0)
      setPaidDays(0)
      setExpectedContractValue(0)
      setTotalCollectedOverall(0)
    } finally {
      setLoading(false)
    }
  }

  const exportToExcel = () => {
    if (!reportData.length) return
    const ws = XLSX.utils.json_to_sheet(
      reportData.map((p) => ({
        Date: p.payment_date,
        Amount: num(p.amount),
        Status: p.status,
        'Received At': p.received_at || '-',
      }))
    )
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Specific Report')
    XLSX.writeFile(wb, `specific_vehicle_report_${selectedVehicleId}.xlsx`)
  }

  const exportToPDF = () => {
    if (!reportData.length) return
    const doc = new jsPDF({ unit: 'pt', format: 'a4' })
    const title = 'Specific Vehicle Payment Report'
    const range = `Range: ${fromDate || 'N/A'} — ${toDate || 'N/A'}`
    const stamp = `Generated: ${new Date().toLocaleString()}`

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(18)
    doc.text(title, 48, 56)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(11)
    doc.text(range, 48, 78)
    doc.text(stamp, 48, 94)

    autoTable(doc, {
      startY: 116,
      theme: 'grid',
      head: [['Date', 'Amount (TZS)', 'Status', 'Received At']],
      body: reportData.map((p) => [
        p.payment_date,
        fmt(p.amount),
        p.status,
        p.received_at || '-',
      ]),
      styles: {
        font: 'helvetica',
        fontSize: 10,
        lineColor: [140, 140, 140],
        lineWidth: 0.6,
        cellPadding: { top: 6, right: 6, bottom: 6, left: 6 },
      },
      headStyles: {
        fillColor: [33, 33, 33],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      margin: { left: 48, right: 48 },
    })

    doc.save(`specific_vehicle_report_${selectedVehicleId}.pdf`)
  }

  const handlePrint = () => {
    const contents = printRef.current?.innerHTML || ''
    const w = window.open('', '', 'width=900,height=600')
    if (!w) return
    w.document.write(`
      <html>
        <head>
          <title>Print Vehicle Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h2, h3 { margin: 0 0 8px; }
            .summary { background: #f7f7f7; padding: 12px; border-radius: 8px; }
            table { border-collapse: collapse; width: 100%; margin-top: 16px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
            th { background: #eee; }
          </style>
        </head>
        <body>${contents}</body>
      </html>
    `)
    w.document.close()
    w.focus()
    w.print()
    w.close()
  }

  const pagedRows = reportData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  // compact summary row
  const SummaryRow = ({ label, value, bold }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        py: 0.4,
        borderBottom: '1px solid #eee',
      }}
    >
      <Typography sx={{ color: 'text.secondary', minWidth: 190 }}>
        {label}:
      </Typography>
      <Typography sx={{ fontWeight: bold ? 700 : 500 }}>{value}</Typography>
    </Box>
  )

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        📝 Specific Vehicle Report
      </Typography>

      {errorMsg && (
        <Typography sx={{ mb: 2 }} color="warning.main">
          {errorMsg}
        </Typography>
      )}

      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <Grid container spacing={2} alignItems="center">
          {/* Vehicle selector */}
          <Grid item xs={12} md={5}>
            <Autocomplete
              options={vehicles}
              getOptionLabel={(o) =>
                o?.plate_number
                  ? `${o.plate_number}${
                      o.driver_name ? ` — ${o.driver_name}` : ''
                    }`
                  : ''
              }
              isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
              value={
                vehicles.find(
                  (v) => String(v.id) === String(selectedVehicleId)
                ) || null
              }
              onChange={(_e, val) => handleVehicleSelect(val)}
              loading={loadingVehicles}
              disablePortal
              clearOnEscape
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {option.plate_number}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.driver_name || '—'}
                      {option.contract_start
                        ? ` • start ${String(
                            option.contract_start
                          ).slice(0, 10)}`
                        : ''}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vehicle"
                  placeholder="Search plate or driver…"
                  fullWidth
                  size="medium"
                  sx={{ minWidth: { xs: '100%', md: 420 } }}
                  InputProps={{
                    ...params.InputProps,
                    sx: { height: 52, pr: 1.5 },
                    endAdornment: (
                      <>
                        {loadingVehicles ? (
                          <CircularProgress size={18} sx={{ mr: 1 }} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              ListboxProps={{ style: { maxHeight: 320 } }}
            />
          </Grid>

          <Grid item xs={12} sm={4} md={2.5}>
            <TextField
              type="date"
              label="From"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={4} md={2.5}>
            <TextField
              type="date"
              label="To"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md="auto">
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: { xs: 1, md: 0 }, flexWrap: 'wrap' }}
            >
              <Button
                variant="contained"
                onClick={generateReport}
                disabled={loading || !selectedVehicleId}
              >
                {loading ? 'Loading…' : 'Generate'}
              </Button>
              <Button
                variant="outlined"
                onClick={exportToExcel}
                disabled={!reportData.length}
              >
                Export Excel
              </Button>
              <Button
                variant="outlined"
                onClick={exportToPDF}
                disabled={!reportData.length}
              >
                Export PDF
              </Button>
              <Button
                variant="outlined"
                onClick={handlePrint}
                disabled={!vehicleInfo}
              >
                Print All
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <div ref={printRef}>
        {vehicleInfo && (
          <>
            {/* Summary */}
            <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }} variant="outlined">
              <Typography variant="h6" sx={{ mb: 1 }}>
                📊 Summary
              </Typography>

              <SummaryRow label="Plate Number" value={vehicleInfo.plate_number} />
              <SummaryRow
                label="Driver"
                value={vehicleInfo.driver_name || '—'}
              />
              <SummaryRow
                label="Daily Rate"
                value={`${fmt(vehicleInfo.daily_rate)} TZS`}
              />
              <SummaryRow
                label="Purchase Price"
                value={
                  vehicleInfo.purchase_price != null
                    ? `${fmt(vehicleInfo.purchase_price)} TZS`
                    : 'N/A'
                }
              />
              <SummaryRow
                label="Contract Start"
                value={
                  vehicleInfo.contract_start
                    ? String(vehicleInfo.contract_start).slice(0, 10)
                    : '—'
                }
              />
              <SummaryRow
                label="Contract End"
                value={
                  vehicleInfo.contract_end
                    ? String(vehicleInfo.contract_end).slice(0, 10)
                    : '—'
                }
              />
              <SummaryRow label="Active Days (Period)" value={activeDays} />
              <SummaryRow label="Paid Days (Period)" value={paidDays} />
              <SummaryRow
                label="Total Collected (Period)"
                value={`${fmt(totalAmount)} TZS`}
              />
              <SummaryRow
                label="Expected (Period)"
                value={`${fmt(expectedAmount)} TZS`}
              />
              <SummaryRow
                label="Profit / Loss (Period)"
                value={`${fmt(profitLoss)} TZS`}
                bold
              />
              <SummaryRow
                label="Total Collected (Overall)"
                value={`${fmt(totalCollectedOverall)} TZS`}
              />
              <SummaryRow
                label="Expected Overall (Contract)"
                value={`${fmt(expectedContractValue)} TZS`}
              />
              <SummaryRow
                label="Profit / Loss (Overall vs Contract)"
                value={`${(
                  num(totalCollectedOverall) - num(expectedContractValue)
                ).toLocaleString()} TZS`}
                bold
              />
            </Paper>

            {reportData.length > 0 && (
              <Paper variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f7f7f7' }}>
                      <TableCell>
                        <strong>Date</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Amount (TZS)</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Status</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Received At</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedRows.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{p.payment_date}</TableCell>
                        <TableCell align="right">{fmt(p.amount)}</TableCell>
                        <TableCell>{p.status}</TableCell>
                        <TableCell>{p.received_at || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <TablePagination
                  component="div"
                  count={reportData.length}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10))
                    setPage(0)
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </Paper>
            )}
          </>
        )}
      </div>

      {loadingVehicles && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!selectedVehicleId && vehicles.length > 0 && (
        <Typography sx={{ mt: 1 }} color="text.secondary">
          Select a vehicle to begin.
        </Typography>
      )}
    </Box>
  )
}